import { useEffect, useState } from "react";
import {
  Box, Typography, Paper, Stack, Button, Alert, TextField, InputAdornment,
  Dialog, DialogTitle, DialogContent, DialogActions, IconButton, Chip,
  Menu, MenuItem, ListItemIcon, ListItemText, CircularProgress, FormControlLabel, Switch,
  Grid2 as Grid,
} from "@mui/material";
import { DataGrid, type GridColDef, type GridPaginationModel } from "@mui/x-data-grid";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { useForm, Controller } from "react-hook-form";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { servicesApi, type ServiceDto } from "@/api/endpoints";
import { errorMessage } from "@/api/client";
import { useAuth } from "@/store/auth";
import { useTaxRegime } from "@/shared/hooks/useTaxRegime";

const inr = new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR" });

export function ServicesPage() {
  const qc = useQueryClient();
  const [search, setSearch] = useState("");
  const [pagination, setPagination] = useState<GridPaginationModel>({ page: 0, pageSize: 25 });
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<ServiceDto | null>(null);
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [menuRow, setMenuRow] = useState<ServiceDto | null>(null);

  const canManage = useAuth(s => s.hasPermission("sales.service.manage"));

  const query = useQuery({
    queryKey: ["services", search, pagination.page, pagination.pageSize],
    queryFn: () => servicesApi.list({
      search: search || undefined,
      page: pagination.page + 1, pageSize: pagination.pageSize,
    }),
  });

  const deleteMutation = useMutation({
    meta: { successMessage: "Service deleted" },
    mutationFn: (id: string) => servicesApi.delete(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["services"] }),
  });

  const openCreate = () => { setEditing(null); setFormOpen(true); };
  const openEdit = (s: ServiceDto) => { setEditing(s); setFormOpen(true); setMenuAnchor(null); };
  const closeMenu = () => { setMenuAnchor(null); setMenuRow(null); };
  const handleDelete = (s: ServiceDto) => {
    closeMenu();
    if (confirm(`Delete service "${s.code} — ${s.name}"?`)) deleteMutation.mutate(s.id);
  };

  const columns: GridColDef<ServiceDto>[] = [
    { field: "code", headerName: "Code", width: 140 },
    { field: "name", headerName: "Name", flex: 1, minWidth: 220 },
    { field: "sacCode", headerName: "SAC", width: 110 },
    {
      field: "defaultRate", headerName: "Default rate", width: 130, align: "right", headerAlign: "right",
      valueFormatter: (v: number) => inr.format(v ?? 0),
    },
    {
      field: "gstRate", headerName: "Tax %", width: 90, align: "right", headerAlign: "right",
      valueFormatter: (v: number) => `${v ?? 0}%`,
    },
    {
      field: "isActive", headerName: "Status", width: 110,
      renderCell: (p) => (
        <Chip size="small" label={p.row.isActive ? "Active" : "Inactive"}
          color={p.row.isActive ? "success" : "default"} variant="outlined" />
      ),
    },
    {
      field: "actions", headerName: "", width: 60, sortable: false, filterable: false,
      renderCell: (p) => (
        <IconButton size="small" onClick={(e) => { setMenuAnchor(e.currentTarget); setMenuRow(p.row); }}>
          <MoreVertIcon fontSize="small" />
        </IconButton>
      ),
    },
  ];

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Typography variant="h5" fontWeight={600}>Services</Typography>
        {canManage && (
          <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>New service</Button>
        )}
      </Stack>
      <Paper sx={{ p: 2 }}>
        <TextField
          placeholder="Search by code or name"
          fullWidth size="small" value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
          sx={{ mb: 2 }}
        />
        {query.isError && <Alert severity="error" sx={{ mb: 2 }}>{errorMessage(query.error)}</Alert>}
        {deleteMutation.isError && <Alert severity="error" sx={{ mb: 2 }}>{errorMessage(deleteMutation.error)}</Alert>}
        <Box sx={{ height: 600 }}>
          <DataGrid
            rows={query.data?.items ?? []}
            columns={columns}
            loading={query.isLoading}
            paginationMode="server"
            rowCount={query.data?.totalCount ?? 0}
            paginationModel={pagination}
            onPaginationModelChange={setPagination}
            pageSizeOptions={[25, 50, 100]}
            disableRowSelectionOnClick
            getRowId={(row) => row.id}
            onRowDoubleClick={(p) => canManage && openEdit(p.row)}
          />
        </Box>
      </Paper>

      <Menu anchorEl={menuAnchor} open={!!menuAnchor} onClose={closeMenu}>
        {canManage && (
          <MenuItem onClick={() => menuRow && openEdit(menuRow)}>
            <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Edit</ListItemText>
          </MenuItem>
        )}
        {canManage && (
          <MenuItem onClick={() => menuRow && handleDelete(menuRow)}>
            <ListItemIcon><DeleteIcon fontSize="small" color="error" /></ListItemIcon>
            <ListItemText sx={{ color: "error.main" }}>Delete</ListItemText>
          </MenuItem>
        )}
      </Menu>

      <ServiceDialog open={formOpen} onClose={() => setFormOpen(false)} service={editing} />
    </Box>
  );
}

type ServiceFormData = {
  code: string; name: string; sacCode: string;
  defaultRate: number; gstRate: number; isActive: boolean;
};
const emptyService: ServiceFormData = {
  code: "", name: "", sacCode: "", defaultRate: 0, gstRate: 18, isActive: true,
};

function ServiceDialog({ open, onClose, service }: { open: boolean; onClose: () => void; service: ServiceDto | null }) {
  const qc = useQueryClient();
  const isEdit = !!service;
  const { taxLabel } = useTaxRegime();
  const { control, handleSubmit, reset, formState: { errors } } =
    useForm<ServiceFormData>({ defaultValues: emptyService });

  useEffect(() => {
    if (!open) return;
    reset(service ? {
      code: service.code, name: service.name, sacCode: service.sacCode ?? "",
      defaultRate: service.defaultRate, gstRate: service.gstRate, isActive: service.isActive,
    } : emptyService);
  }, [open, service, reset]);

  const mutation = useMutation({
    meta: { successMessage: isEdit ? "Service updated" : "Service created" },
    mutationFn: async (data: ServiceFormData) => {
      const payload = {
        code: data.code.trim(), name: data.name.trim(),
        sacCode: data.sacCode.trim() || undefined,
        defaultRate: data.defaultRate, gstRate: data.gstRate, isActive: data.isActive,
      };
      if (isEdit && service) { await servicesApi.update(service.id, payload); return service.id; }
      const r = await servicesApi.create(payload);
      return r.id;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["services"] });
      onClose();
    },
  });

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <form onSubmit={handleSubmit((d) => mutation.mutate(d))}>
        <DialogTitle>{isEdit ? "Edit service" : "New service"}</DialogTitle>
        <DialogContent dividers>
          {mutation.isError && <Alert severity="error" sx={{ mb: 2 }}>{errorMessage(mutation.error)}</Alert>}
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 4 }}>
              <Controller name="code" control={control} rules={{ required: "Required", maxLength: 50 }}
                render={({ field }) => (
                  <TextField {...field} label="Code *" fullWidth size="small" disabled={isEdit}
                    error={!!errors.code} helperText={errors.code?.message ?? (isEdit ? "Cannot change" : undefined)} />
                )} />
            </Grid>
            <Grid size={{ xs: 12, sm: 8 }}>
              <Controller name="name" control={control} rules={{ required: "Required", maxLength: 200 }}
                render={({ field }) => (
                  <TextField {...field} label="Name *" fullWidth size="small"
                    error={!!errors.name} helperText={errors.name?.message} />
                )} />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <Controller name="sacCode" control={control} rules={{ maxLength: 20 }}
                render={({ field }) => <TextField {...field} label="SAC code" fullWidth size="small" />} />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <Controller name="defaultRate" control={control}
                rules={{ min: { value: 0, message: ">= 0" } }}
                render={({ field }) => (
                  <TextField {...field} type="number" label="Default rate (₹)" fullWidth size="small"
                    onChange={(e) => field.onChange(e.target.value === "" ? 0 : Number(e.target.value))}
                    error={!!errors.defaultRate} helperText={errors.defaultRate?.message} />
                )} />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <Controller name="gstRate" control={control}
                rules={{ min: { value: 0, message: "0-28" }, max: { value: 28, message: "0-28" } }}
                render={({ field }) => (
                  <TextField {...field} type="number" label={`${taxLabel} rate (%)`} fullWidth size="small"
                    onChange={(e) => field.onChange(e.target.value === "" ? 0 : Number(e.target.value))}
                    error={!!errors.gstRate} helperText={errors.gstRate?.message} />
                )} />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Controller name="isActive" control={control}
                render={({ field }) => (
                  <FormControlLabel label="Active"
                    control={<Switch checked={field.value} onChange={(_, v) => field.onChange(v)} />} />
                )} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="contained" disabled={mutation.isPending}
            startIcon={mutation.isPending ? <CircularProgress size={16} color="inherit" /> : null}>
            {isEdit ? "Save" : "Create"}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
